import * as THREE from 'three'
import { createCutCrossSection, createLineSegmentBetween, createPointMarker } from '@/graphics/geometry'
import { BnBManager, type BnBNode } from '@/game/bnb-manager'
import { bnbUI } from '@/game/bnb-ui'
import { uiManager } from '@/game/ui-manager'
import { appBus } from '@/app/app-events'
import { logger } from '@/app/logger'
import { useGameStore } from '@/react/state/gameStore'
import {
  clearBnBGeometryCache,
  computeBnBGeometryPayloadCached,
} from '@/game/engine/bnb-geometry-compute'
import { assembleBnBScene, getBnBMeshCache } from '@/game/engine/bnb-scene-assembler'
import { countAndDisposeObject3D, countObject3DResources } from '@/game/engine/object3d-resource-utils'

export interface BnBVisualizationEngineContext {
  bnbManager: BnBManager | null
  currentProblem: BnBNode['problem'] | null
  currentCuttingPlane: unknown | null
  pendingCut: unknown | null
  pendingCutIndex: number
  pendingSimplexConfig: unknown | null
  bnbActive: boolean
  appBusUnsubscribers: Array<() => void>
  scene: THREE.Scene
  branchOverlays: THREE.Object3D[]
  polyhedronGroup: THREE.Group | null
  polyManager: {
    disposeGroup(group: THREE.Group | null): void
    buildGroupFromInequalities(inequalities: BnBNode['problem']['inequalities'], options?: { equalityPlaneFallback?: boolean }): THREE.Group
    addIntegerPointsAndAxes(
      group: THREE.Group,
      problem: BnBNode['problem'],
      options?: { showHull?: boolean; overrideLPSolution?: number[]; hasFractionalSolution?: boolean },
    ): void
    addLPMarker(group: THREE.Group, solution: number[], isFractional: boolean): void
  }
  performanceProbe?: { markStart?: (name: string) => void; markEnd?: (name: string) => void }
  resourceCounters?: {
    addOverlay?: (count?: number) => void
    clearOverlay?: (count?: number) => void
    geometryCreated?: (count?: number) => void
    geometryDisposed?: (count?: number) => void
    materialCreated?: (count?: number) => void
    materialDisposed?: (count?: number) => void
  }
  renderScheduler?: { invalidate?: (reason: string) => void }
  cleanupCuttingPlane(): void
  clearBranchOverlays(): void
  renderBnBNode(node: BnBNode): void
  getBnBMaxNodesFromStorage(): number
  storeBnBMaxNodes(limit: number): void
}

export function clearBnBCaches(): void {
  clearBnBGeometryCache()
  getBnBMeshCache().clearAll()
}

export function setupBnBIntegration(engine: BnBVisualizationEngineContext): void {
  engine.bnbManager = new BnBManager(7)

  const unsubscribeStartRequested = appBus.on('bnb-start-requested', () => {
    if (!engine.currentProblem || !engine.bnbManager) return

    // Entering BnB should always clear any active cut preview plane.
    const hadPendingCut = !!engine.pendingCut || engine.pendingCutIndex !== -1 || !!engine.pendingSimplexConfig
    if (engine.currentCuttingPlane) {
      engine.cleanupCuttingPlane()
    }
    engine.pendingCut = null
    engine.pendingCutIndex = -1
    engine.pendingSimplexConfig = null
    if (hadPendingCut || engine.currentCuttingPlane) {
      useGameStore.getState().dispatchCutFlowEvent({ type: 'CUT_CANCELLED' })
    }

    useGameStore.getState().dispatchBnBFlowEvent({ type: 'BNB_START_REQUESTED' })
    clearBnBCaches()
    engine.bnbActive = true
    const st = engine.bnbManager.start(engine.currentProblem, engine.getBnBMaxNodesFromStorage())
    bnbUI.setMaxNodes(st.maxNodes)
  })
  engine.appBusUnsubscribers.push(unsubscribeStartRequested)

  const unsubscribeBranchRequested = appBus.on('bnb-branch-requested', ({ varIndex, mode, side, nodeId }) => {
    if (!engine.bnbManager) return
    useGameStore.getState().dispatchBnBFlowEvent({ type: 'BNB_BRANCH_REQUESTED' })
    if (nodeId) {
      engine.bnbManager.selectNode(nodeId)
    }

    if (mode === 'one') {
      const branchSide = side ?? 'le'
      const child = engine.bnbManager.branchOne(varIndex, branchSide)
      if (!child) {
        uiManager.showNotification('Cannot branch: node closed or limit reached', 'info')
        useGameStore.getState().dispatchBnBFlowEvent({ type: 'BNB_COMMAND_COMPLETED' })
      }
      return
    }

    const { left, right } = engine.bnbManager.branchOnVariable(varIndex)
    const pick = left || right
    if (!pick) {
      uiManager.showNotification('Cannot branch both: node closed or limit reached', 'info')
      useGameStore.getState().dispatchBnBFlowEvent({ type: 'BNB_COMMAND_COMPLETED' })
    }
  })
  engine.appBusUnsubscribers.push(unsubscribeBranchRequested)

  const unsubscribeSelectRequested = appBus.on('bnb-select-requested', ({ nodeId }) => {
    if (!engine.bnbManager) return
    useGameStore.getState().dispatchBnBFlowEvent({ type: 'BNB_NODE_SELECTED' })
    engine.bnbManager.selectNode(nodeId)
  })
  engine.appBusUnsubscribers.push(unsubscribeSelectRequested)

  const unsubscribeBacktrackRequested = appBus.on('bnb-backtrack-requested', ({ nodeId }) => {
    if (!engine.bnbManager) return
    useGameStore.getState().dispatchBnBFlowEvent({ type: 'BNB_BACKTRACK_REQUESTED' })
    engine.bnbManager.backtrack(nodeId)
  })
  engine.appBusUnsubscribers.push(unsubscribeBacktrackRequested)

  const unsubscribeActivated = appBus.on('bnb-node-activated', ({ nodeId, node }) => {
    const resolved = node || engine.bnbManager?.getState()?.nodes.get(nodeId)
    if (resolved) {
      try {
        engine.renderBnBNode(resolved)
      } catch (e) {
        logger.error('[BnB] render on activation failed', e)
      }
    }
  })
  engine.appBusUnsubscribers.push(unsubscribeActivated)

  const unsubscribeResetRequested = appBus.on('bnb-reset-requested', () => {
    try {
      useGameStore.getState().dispatchBnBFlowEvent({ type: 'BNB_RESET_REQUESTED' })
      clearBnBCaches()
      engine.clearBranchOverlays()
      engine.bnbActive = false
      engine.bnbManager = new BnBManager(engine.getBnBMaxNodesFromStorage())
      appBus.emit('bnb-reset', {})
    } catch (e) {
      logger.error('[BnB] reset error', e)
    }
  })
  engine.appBusUnsubscribers.push(unsubscribeResetRequested)

  const unsubscribeBacktracked = appBus.on('bnb-backtracked', () => {
    clearBnBCaches()
  })
  engine.appBusUnsubscribers.push(unsubscribeBacktracked)

  const unsubscribeNodeUpdated = appBus.on('bnb-node-updated', ({ node }) => {
    clearBnBGeometryCache(node.id)
  })
  engine.appBusUnsubscribers.push(unsubscribeNodeUpdated)
}

export function renderBnBNode(engine: BnBVisualizationEngineContext, node: BnBNode): void {
  engine.performanceProbe?.markStart?.('renderBnBNode')

  const bnbState = engine.bnbManager?.getState()
  const currentNodeId = useGameStore.getState().ui.bnbState.currentNodeId
  const revision = bnbState
    ? [
      bnbState.stats.created,
      bnbState.stats.pruned,
      bnbState.stats.fathomed,
      bnbState.nodes.size,
      node.status,
      node.children.length,
      node.lp?.optimalValue ?? 'na',
    ].join(':')
    : 'no-state'
  const payload = computeBnBGeometryPayloadCached(node.id, node, bnbState?.nodes, revision)

  assembleBnBScene(engine, node, payload.children, currentNodeId)

  engine.clearBranchOverlays()

  const SHOW_BNB_PLANES = true
  if (SHOW_BNB_PLANES && payload.ghostPlanes.length > 0 && engine.currentProblem) {
    const problemForSlices = engine.currentProblem
    const margin = 0.5

    payload.ghostPlanes.forEach((spec) => {
      const alpha = [0, 0, 0]
      alpha[spec.varIndex] = 1
      const beta = spec.value

      let width = 1
      let height = 1
      const position = new THREE.Vector3()
      const rotation = new THREE.Euler()

      if (spec.varIndex === 0) {
        width = spec.aabb.zMax - spec.aabb.zMin + margin * 2
        height = spec.aabb.yMax - spec.aabb.yMin + margin * 2
        position.set(spec.value, (spec.aabb.yMin + spec.aabb.yMax) / 2, (spec.aabb.zMin + spec.aabb.zMax) / 2)
        rotation.set(0, Math.PI / 2, 0)
      } else if (spec.varIndex === 1) {
        width = spec.aabb.xMax - spec.aabb.xMin + margin * 2
        height = spec.aabb.zMax - spec.aabb.zMin + margin * 2
        position.set((spec.aabb.xMin + spec.aabb.xMax) / 2, spec.value, (spec.aabb.zMin + spec.aabb.zMax) / 2)
        rotation.set(-Math.PI / 2, 0, 0)
      } else {
        width = spec.aabb.xMax - spec.aabb.xMin + margin * 2
        height = spec.aabb.yMax - spec.aabb.yMin + margin * 2
        position.set((spec.aabb.xMin + spec.aabb.xMax) / 2, (spec.aabb.yMin + spec.aabb.yMax) / 2, spec.value)
        rotation.set(0, 0, 0)
      }

      const geometry = new THREE.PlaneGeometry(width, height)
      const material = new THREE.MeshStandardMaterial({
        color: 0x22d3ee,
        transparent: true,
        opacity: 0.05,
        side: THREE.DoubleSide,
        depthWrite: false,
        metalness: 0.1,
        roughness: 0.4,
        emissive: 0x22d3ee,
        emissiveIntensity: 0.05,
      })

      const ghostPlane = new THREE.Mesh(geometry, material)
      ghostPlane.position.copy(position)
      ghostPlane.rotation.copy(rotation)

      const edges = new THREE.EdgesGeometry(geometry)
      const lineMat = new THREE.LineBasicMaterial({ color: 0x22d3ee, transparent: true, opacity: 0.15 })
      const wireframe = new THREE.LineSegments(edges, lineMat)
      ghostPlane.add(wireframe)

      engine.scene.add(ghostPlane)
      engine.branchOverlays.push(ghostPlane)
      engine.resourceCounters?.addOverlay?.(1)
      const ghostPlaneResources = countObject3DResources(ghostPlane)
      engine.resourceCounters?.geometryCreated?.(ghostPlaneResources.geometries)
      engine.resourceCounters?.materialCreated?.(ghostPlaneResources.materials)

      let slice: THREE.Object3D | null = null
      try {
        slice = createCutCrossSection(problemForSlices.inequalities, alpha, beta)
      } catch {
        slice = null
      }

      if (slice) {
        engine.scene.add(slice)
        engine.branchOverlays.push(slice)
        engine.resourceCounters?.addOverlay?.(1)
        const sliceResources = countObject3DResources(slice)
        engine.resourceCounters?.geometryCreated?.(sliceResources.geometries)
        engine.resourceCounters?.materialCreated?.(sliceResources.materials)
      }
    })
  }

  try {
    if (payload.children.length > 0) {
      payload.childDegeneracy.forEach((item) => {
        if (item.vertices.length === 1) {
          const marker = createPointMarker(item.vertices[0])
          engine.scene.add(marker)
          engine.branchOverlays.push(marker)
          engine.resourceCounters?.addOverlay?.(1)
          const markerResources = countObject3DResources(marker)
          engine.resourceCounters?.geometryCreated?.(markerResources.geometries)
          engine.resourceCounters?.materialCreated?.(markerResources.materials)
        } else if (item.vertices.length === 2) {
          const seg = createLineSegmentBetween(item.vertices[0], item.vertices[1])
          engine.scene.add(seg)
          engine.branchOverlays.push(seg)
          engine.resourceCounters?.addOverlay?.(1)
          const segResources = countObject3DResources(seg)
          engine.resourceCounters?.geometryCreated?.(segResources.geometries)
          engine.resourceCounters?.materialCreated?.(segResources.materials)
        }
      })
    } else {
      if (payload.currentDegeneracy.length === 1) {
        const marker = createPointMarker(payload.currentDegeneracy[0])
        engine.scene.add(marker)
        engine.branchOverlays.push(marker)
        engine.resourceCounters?.addOverlay?.(1)
        const markerResources = countObject3DResources(marker)
        engine.resourceCounters?.geometryCreated?.(markerResources.geometries)
        engine.resourceCounters?.materialCreated?.(markerResources.materials)
      } else if (payload.currentDegeneracy.length === 2) {
        const seg = createLineSegmentBetween(payload.currentDegeneracy[0], payload.currentDegeneracy[1])
        engine.scene.add(seg)
        engine.branchOverlays.push(seg)
        engine.resourceCounters?.addOverlay?.(1)
        const segResources = countObject3DResources(seg)
        engine.resourceCounters?.geometryCreated?.(segResources.geometries)
        engine.resourceCounters?.materialCreated?.(segResources.materials)
      }
    }
  } catch {
    // ignore degeneracy rendering errors
  }

  engine.performanceProbe?.markEnd?.('renderBnBNode')
  engine.renderScheduler?.invalidate?.('bnb-node-rendered')
}

export function clearBranchOverlays(engine: BnBVisualizationEngineContext): void {
  if (engine.branchOverlays.length === 0) return
  const overlayCount = engine.branchOverlays.length
  let disposedGeometries = 0
  let disposedMaterials = 0

  for (const obj of engine.branchOverlays) {
    engine.scene.remove(obj)
    const disposed = countAndDisposeObject3D(obj)
    disposedGeometries += disposed.geometries
    disposedMaterials += disposed.materials
  }

  engine.branchOverlays = []
  engine.resourceCounters?.geometryDisposed?.(disposedGeometries)
  engine.resourceCounters?.materialDisposed?.(disposedMaterials)
  engine.resourceCounters?.clearOverlay?.(overlayCount)
  engine.renderScheduler?.invalidate?.('bnb-overlays-cleared')
}
